// components/CommentCard.tsx

import { trpc } from '@web/lib/trpc';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';

import { toast } from 'sonner';

import { Settings2 } from 'lucide-react';
import { Button } from '@web/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@web/components/ui/dropdown-menu';
import { toastConfirm } from '@web/utils';

type Comment = {
  id: string;
  createdBy: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  content: string;
  createdAt: string; // ISO date string
};

type CommentCardProps = {
  comment: Comment;
  setActiveCommentId: (commentId: string | null) => void;
};

export function CommentCard({ comment, setActiveCommentId }: CommentCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const utils = trpc.useUtils();

  const { mutateAsync: deleteComment } = trpc.comment.deleteComment.useMutation(
    {
      onSuccess: async () => {
        await utils.comment.listAllCommentsOnRecord.invalidate();
        toast.success('Comment deleted successfully');
      },
      onError: (error) => {
        toast.error('Error deleting comment', { description: error.message });
      },
    },
  );

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const truncatedContent =
    comment.content.length > 100
      ? `${comment.content.substring(0, 100)}...`
      : comment.content;

  const handleDeleteComment = useCallback(() => {
    toastConfirm({
      message: 'Sure you want to delete this comment?',

      callback: () => {
        void deleteComment({ id: comment.id });
      },
    });
  }, [comment.id, deleteComment]);

  return (
    <>
      <div className="flex flex-col space-y-2 rounded-lg bg-white p-4 shadow-md">
        <div className="flex items-center space-x-3">
          {comment.createdBy.avatarUrl ? (
            <img
              src={comment.createdBy.avatarUrl}
              alt={`${comment.createdBy.name}'s avatar`}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-300 text-white">
              {comment.createdBy.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">
              {comment.createdBy.name}
            </p>
            <p className="text-xs text-gray-500">
              {format(new Date(comment.createdAt), 'MM/dd/yyyy')}
            </p>
          </div>
          {/* Dropdown menu for actions like edit/delete */}
          <DropdownMenu>
            <DropdownMenuTrigger aria-label="Comment Actions">
              <Settings2 className="size-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {/* Edit Option */}
              <DropdownMenuItem
                onSelect={() => {
                  setActiveCommentId(comment.id);
                }}
              >
                Edit
              </DropdownMenuItem>
              {/* Delete Option */}
              <DropdownMenuItem onSelect={handleDeleteComment}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="text-gray-700">
          <p>{isExpanded ? comment.content : truncatedContent}</p>

          {comment.content.length > 50 && (
            <div className="flex justify-end">
              <Button
                variant="link"
                onClick={toggleExpand}
                className="ml-2 text-right text-sm hover:underline focus:outline-none"
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Show less' : 'Show more'}
              >
                {isExpanded ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
